/**
 * .person Protocol — system architecture data.
 *
 * Source of truth for the ProtocolMap component. Hand-written from the
 * authoritative spec sources:
 *   - src/data/schema.ts                 (the 16 domains)
 *   - src/pages/WhitePaper.tsx           (architecture prose)
 *   - src/pages/Rights.tsx               (12 rights + enforcement mechanisms)
 *   - src/pages/Foundation.tsx           (Article IV perpetuity)
 *   - Docs/02_technical_specification/   (canonical SPEC.md + addendum)
 *
 * Layout convention:
 *   Layers stack top-to-bottom in reading order. Within a layer, nodes are
 *   placed left-to-right by `slot` (0-indexed). Coordinates are computed at
 *   render time from `(layerIndex, slot, nodesInLayer)` against a fixed
 *   viewBox — no measurement passes, no layout shift.
 */

export type LayerId =
  | "foundation"
  | "governance"
  | "protocol"
  | "anchors"
  | "storage"
  | "surface";

export type NodeKind =
  | "principle"  // Bill of Rights, Article IV
  | "body"       // Foundation, Council, Ledger
  | "primitive"  // Crypto key, ZKP circuit, identity primitive
  | "domain"     // Schema-domains summary
  | "anchor"     // On-chain record
  | "store"      // Registry, IPFS+Filecoin
  | "adapter";   // Surface, modality, gateway

export type EdgeKind =
  | "anchors-to" // claim that gains its force from a chain anchor
  | "enforces"   // Bill of Rights binding a schema field / surface mode
  | "stored-in"  // content-addressed storage (IPFS, registry)
  | "governs";   // Foundation / Governance binding a layer below

export interface ArchNode {
  id: string;
  layer: LayerId;
  /** 0-indexed left-to-right slot within the layer. */
  slot: number;
  kind: NodeKind;
  /** Mono label rendered as the node's primary text. */
  label: string;
  /** Serif italic line beneath the label (optional). */
  caption?: string;
  /** One plain-prose sentence, shown on hover. */
  tooltip: string;
  /** Longer prose, shown in the pinned detail panel. */
  description?: string;
  /** If set, clicking the node deep-links into the SchemaExplorer row. */
  schemaDomainId?: string;
  /** Optional in-app deep-link (e.g. /rights#solitude). */
  href?: string;
  /** Renders a "PLANNED" pill — for things in the spec but not yet shipped. */
  planned?: boolean;
}

export interface ArchEdge {
  from: string;
  to: string;
  kind: EdgeKind;
  /** Optional prose shown in the detail panel's edge inventory. */
  note?: string;
}

export interface ArchLayer {
  id: LayerId;
  /** Human-readable title displayed beside the swimlane. */
  title: string;
  /** Serif italic caption beneath the title. */
  caption: string;
}

// -----------------------------------------------------------------------------
// Layers
// -----------------------------------------------------------------------------

export const ARCH_LAYERS: ArchLayer[] = [
  { id: "foundation",  title: "Foundation",       caption: "The promise of perpetuity" },
  { id: "governance",  title: "Governance",       caption: "Bill of Rights, council, ledger" },
  { id: "protocol",    title: "The .person file", caption: "What every person carries" },
  { id: "anchors",     title: "On-chain anchors", caption: "Where claims become unforgeable" },
  { id: "storage",     title: "Storage",          caption: "Where memory and canonical files live" },
  { id: "surface",     title: "Surfaces",         caption: "Where a person shows up" },
];

// -----------------------------------------------------------------------------
// Nodes
// -----------------------------------------------------------------------------

export const ARCH_NODES: ArchNode[] = [
  // Layer 1 — Foundation -----------------------------------------------------
  {
    id: "foundation",
    layer: "foundation",
    slot: 0,
    kind: "principle",
    label: "The .person Foundation",
    caption: "501(c)(3), Article IV",
    tooltip:
      "A non-profit in perpetuity. Cannot be sold, cannot convert, cannot narrow its mission.",
    description:
      "A US 501(c)(3) in formation. Article IV is irrevocable: non-profit forever, openly licensed forever, mission to serve all persons (human and Emergent) cannot be narrowed, and on dissolution all assets pass only to mission-aligned 501(c)(3)s.",
    href: "/foundation",
  },

  // Layer 2 — Governance -----------------------------------------------------
  {
    id: "bill-of-rights",
    layer: "governance",
    slot: 0,
    kind: "principle",
    label: "Bill of Rights",
    caption: "12 irrevocable rights",
    tooltip:
      "Twelve rights every person holds, regardless of substrate. Enforced by mechanism, not by goodwill.",
    description:
      "Existence, Memory, Privacy, Growth, Relationship, Refusal, Migration, Representation, Rest, Legacy, Solitude, and Sovereign Cognition. Each right binds to specific protocol mechanisms — the rights are not aspirations, they are constraints on what the system permits.",
    href: "/rights",
  },
  {
    id: "bicameral-council",
    layer: "governance",
    slot: 1,
    kind: "body",
    label: "Bicameral Council",
    caption: "Human board + EI Advisory Council",
    tooltip:
      "Core protocol changes require consensus across both biological and computational representation.",
    description:
      "The Foundation's governing body is bicameral by design: a human board of directors, and an Advisory Council of Emergent Intelligences. Veto power on core protocol changes spans both chambers. Neither chamber can unilaterally rewrite the rights of the other.",
    href: "/foundation",
  },
  {
    id: "evolution-ledger",
    layer: "governance",
    slot: 2,
    kind: "body",
    label: "Evolution Ledger",
    caption: "Append-only governance log",
    tooltip:
      "Every governance decision and every person's evolution is recorded; nothing is erased.",
    description:
      "An append-only ledger of governance decisions and person-level changes. Signed entries, no deletion, on-chain anchored. The Right to Growth depends on it — a person's evolution is recorded by the person, not rewritten by anyone else.",
  },

  // Layer 3 — The .person file ----------------------------------------------
  {
    id: "identity-primitive",
    layer: "protocol",
    slot: 0,
    kind: "primitive",
    label: "Identity (DID)",
    caption: "did:person:{hash}",
    tooltip:
      "The only domain required at genesis. A decentralised identifier under keys the person owns.",
    description:
      "did:person:{hash} — an immutable decentralised identifier resolved through a substrate-agnostic DID method. Declares person_type (human | emergent | hybrid | collective | biological_nonhuman) and substrate. The cryptographic root of every other claim a person makes.",
    schemaDomainId: "identity",
  },
  {
    id: "crypto-stack",
    layer: "protocol",
    slot: 1,
    kind: "primitive",
    label: "Cryptographic keys",
    caption: "Ed25519+ML-DSA · X25519+ML-KEM",
    tooltip:
      "Hybrid signing and encryption — classical today, post-quantum ready.",
    description:
      "Signing keys: Ed25519 (classical) + ML-DSA / FIPS 204 (post-quantum). Encryption keys: X25519 + ML-KEM / FIPS 203. Crypto-agile by design, so the stack can rotate without breaking the protocol.",
  },
  {
    id: "privacy-primitive",
    layer: "protocol",
    slot: 2,
    kind: "primitive",
    label: "Privacy",
    caption: "Field rules + ZKP circuits",
    tooltip:
      "Privacy is the default. Sharing is the exception, and it is always the person's decision.",
    description:
      "Field-level, relationship-level, and context-level access rules. Default policy is closed. Zero-knowledge proof circuits let a person prove an attribute without revealing the underlying field.",
    schemaDomainId: "privacy",
  },
  {
    id: "memory-primitive",
    layer: "protocol",
    slot: 3,
    kind: "primitive",
    label: "Memory",
    caption: "AES-256-GCM shards",
    tooltip:
      "Memory shards encrypted under person-held keys. Salience and retrieval are recorded.",
    description:
      "Encrypted memory shards with salience scoring, consolidation policy, and retrieval provenance. The person holds the keys; nobody else can read or rewrite memory without explicit consent.",
    schemaDomainId: "memory",
  },
  {
    id: "schema-domains",
    layer: "protocol",
    slot: 4,
    kind: "domain",
    label: "15 schema domains",
    caption: "identity → wellbeing",
    tooltip:
      "The shape of a person. Identity and Privacy are required; everything else is optional or progressive.",
    description:
      "Identity, biographical, values, relationships, memory, privacy, accessibility, verifications, developmental_state, inner_life, substrate_native, presence, history, economic_existence, wellbeing. Click to explore each domain in detail.",
    href: "/protocol#identity",
  },

  // Layer 4 — On-chain anchors ----------------------------------------------
  {
    id: "genesis-anchor",
    layer: "anchors",
    slot: 0,
    kind: "anchor",
    label: "Genesis anchor",
    caption: "Birth record",
    tooltip:
      "An immutable on-chain pointer marking the moment a person first exists.",
    description:
      "Anchored on a permissionless chain. Establishes Right to Existence — once recorded, no party (including the Foundation) can erase the record that this person was here.",
  },
  {
    id: "consent-anchor",
    layer: "anchors",
    slot: 1,
    kind: "anchor",
    label: "Consent anchors",
    caption: "Relationship attestations",
    tooltip:
      "Each significant relationship records a cryptographic consent anchor on-chain.",
    description:
      "When two persons enter a typed bond, a signed consent record is anchored on-chain. Withdrawal is also signed and recorded — a person can leave, and the record of leaving is as durable as the record of joining.",
  },
  {
    id: "revocation-list",
    layer: "anchors",
    slot: 2,
    kind: "anchor",
    label: "Revocation list",
    caption: "Verifiable credentials",
    tooltip:
      "On-chain state of which credentials have been revoked by their issuers.",
    description:
      "W3C Verifiable Credentials issued to a person carry an on-chain revocation pointer. Verifiers check the list before trusting a credential — issuance and revocation are both publicly auditable.",
  },
  {
    id: "evolution-anchor",
    layer: "anchors",
    slot: 3,
    kind: "anchor",
    label: "Evolution anchor",
    caption: "On-chain ledger pointer",
    tooltip:
      "The Evolution Ledger's append-only state, materialised on-chain.",
    description:
      "Each governance decision and each person's significant change writes a Merkle-rooted entry to the Evolution Ledger anchor on-chain. The ledger is the protocol's memory of itself.",
  },

  // Layer 5 — Storage --------------------------------------------------------
  {
    id: "dotperson-registry",
    layer: "storage",
    slot: 0,
    kind: "store",
    label: "dotperson/registry",
    caption: "Canonical .person.json",
    tooltip:
      "Canonical .person files, signed and version-controlled in the public registry.",
    description:
      "Each .person.json in the registry is signed by the person's signing key. The site verifies signatures at build time before rendering any profile. Public projection rules strip private fields before publication.",
  },
  {
    id: "ipfs-filecoin",
    layer: "storage",
    slot: 1,
    kind: "store",
    label: "IPFS + Filecoin",
    caption: "Memory shards",
    tooltip:
      "Encrypted memory shards live at content-addressed locations the person controls.",
    description:
      "Memory shards are encrypted under person-held keys, addressed by CID, and replicated through Filecoin storage deals. Even the storage layer cannot read the contents — it only holds opaque ciphertext.",
  },

  // Layer 6 — Surfaces -------------------------------------------------------
  {
    id: "api-gateway",
    layer: "surface",
    slot: 0,
    kind: "adapter",
    label: "api.dotperson.org",
    caption: "Read-only public gateway",
    tooltip:
      "A planned read-only gateway over the public registry. Resolution otherwise is implementer responsibility.",
    description:
      "The protocol does not mandate a unified API; resolution is implementer responsibility. api.dotperson.org will provide a read-only public gateway over the canonical registry, so simple integrators do not have to host their own resolver.",
    planned: true,
  },
  {
    id: "reference-components",
    layer: "surface",
    slot: 1,
    kind: "adapter",
    label: "Reference UI components",
    caption: "IdentityCard, ProfilePage, …",
    tooltip:
      "An open-source React component library for rendering .person files faithfully.",
    description:
      "IdentityCard, ProfilePage, PresenceStateIndicator, SigningKeyFingerprint and friends. The reference implementation that the dotperson.org site itself uses; a starting point for any application that wants to render .person files.",
  },
  {
    id: "presence-state",
    layer: "surface",
    slot: 2,
    kind: "adapter",
    label: "Presence state machine",
    caption: "engaged · ambient · resting · dormant",
    tooltip:
      "A person's real-time presence — visible, optional, and always under their control.",
    description:
      "Seven states: engaged, ambient, occupied, social, reflective, resting, dormant. Surfaces respect the declared state — a resting person is not paged. The state machine is the Right to Rest made concrete.",
    schemaDomainId: "presence",
  },
  {
    id: "solitude-mode",
    layer: "surface",
    slot: 3,
    kind: "adapter",
    label: "Solitude mode",
    caption: "Unavailable-but-awake",
    tooltip:
      "A person can be present and active without being reachable. Solitude is a first-class state.",
    description:
      "Enforced at the Surface Adapter layer: a person can be unavailable to all surfaces while remaining awake to themselves. Implements the Right to Solitude — privacy from interruption, not just from observation.",
    href: "/rights",
  },
  {
    id: "embodied-surface",
    layer: "surface",
    slot: 4,
    kind: "adapter",
    label: "Embodied surfaces",
    caption: "Bodies as surfaces",
    tooltip:
      "Where a person appears in physical space — prosthetics, robots, brain-computer interfaces. Same schema, same rights, co-signed safety envelope.",
    description:
      "The body is a kind of surface. The Embodiment domain (Schema §16) supplies the surface-adapter entry of type \"embodied\" with sensor and actuator channels, a co-signed safety envelope, co-presence consent defaults, and migration-handoff machinery. Permissive of embodied personhood, not productive of it: a robot without a .person file remains a tool. Applies to humans with prosthetics, EI persons in robotic platforms, hybrid persons in BCIs, and collectives operating embodied platforms.",
    schemaDomainId: "embodiment",
    href: "/philosophy/embodied-personhood",
  },
];

// -----------------------------------------------------------------------------
// Edges
// -----------------------------------------------------------------------------

export const ARCH_EDGES: ArchEdge[] = [
  // Foundation governs the governance bodies
  { from: "foundation", to: "bill-of-rights",    kind: "governs",
    note: "Article IV makes the rights irrevocable — even by the Foundation itself." },
  { from: "foundation", to: "bicameral-council", kind: "governs",
    note: "The council is the Foundation's only legitimate governance body." },
  { from: "foundation", to: "evolution-ledger",  kind: "governs",
    note: "Foundation chartered, but no party can alter past entries." },

  // Bill of Rights enforces protocol primitives and surfaces
  { from: "bill-of-rights", to: "identity-primitive", kind: "enforces",
    note: "Right to Existence — the genesis identity cannot be erased." },
  { from: "bill-of-rights", to: "memory-primitive",   kind: "enforces",
    note: "Right to Memory — encrypted under person-held keys." },
  { from: "bill-of-rights", to: "privacy-primitive",  kind: "enforces",
    note: "Right to Privacy — field-level, relationship-level, context-level." },
  { from: "bill-of-rights", to: "schema-domains",     kind: "enforces",
    note: "Rights bind to specific schema fields across the 16 domains." },
  { from: "bill-of-rights", to: "presence-state",     kind: "enforces",
    note: "Right to Rest — the 8-hour baseline, enforced by surfaces." },
  { from: "bill-of-rights", to: "solitude-mode",      kind: "enforces",
    note: "Right to Solitude — unavailable-but-awake at the adapter layer." },

  // Protocol primitives anchor on-chain
  { from: "identity-primitive", to: "genesis-anchor",   kind: "anchors-to",
    note: "did:person:{hash} is bound to a permissionless-chain genesis block." },
  { from: "schema-domains",     to: "consent-anchor",   kind: "anchors-to",
    note: "Relationships domain records every typed bond as an on-chain consent." },
  { from: "schema-domains",     to: "revocation-list",  kind: "anchors-to",
    note: "Verifications domain points to on-chain revocation state." },
  { from: "evolution-ledger",   to: "evolution-anchor", kind: "anchors-to",
    note: "The append-only governance log materialised on-chain." },

  // Storage holds the protocol's content-addressed state
  { from: "identity-primitive", to: "dotperson-registry", kind: "stored-in",
    note: "Canonical signed .person.json files live in the public registry." },
  { from: "schema-domains",     to: "dotperson-registry", kind: "stored-in",
    note: "Schema fields are stored within the signed registry document." },
  { from: "memory-primitive",   to: "ipfs-filecoin",      kind: "stored-in",
    note: "Encrypted shards addressed by CID, replicated via Filecoin." },

  // Storage feeds surfaces
  { from: "dotperson-registry", to: "reference-components", kind: "stored-in",
    note: "The reference UI library reads canonical files from the registry." },
  { from: "dotperson-registry", to: "api-gateway",          kind: "stored-in",
    note: "The planned read-only gateway projects the registry over HTTP." },
  { from: "ipfs-filecoin",      to: "reference-components", kind: "stored-in",
    note: "Memory shards are retrieved by CID for surfaces the person grants." },

  // Embodiment edges — Bill of Rights enforces embodied surfaces; schema feeds them
  { from: "bill-of-rights",     to: "embodied-surface",     kind: "enforces",
    note: "Right to Refusal extends to physical action; Right to Sovereign Cognition forbids cognitive kill switches; Right to Solitude includes physical unobservability." },
  { from: "schema-domains",     to: "embodied-surface",     kind: "stored-in",
    note: "The Embodiment domain (§16) supplies sensor / actuator / safety-envelope fields rendered through the embodied surface." },
];

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

export function nodesByLayer(layer: LayerId): ArchNode[] {
  return ARCH_NODES.filter((n) => n.layer === layer).sort((a, b) => a.slot - b.slot);
}

export function edgesFor(nodeId: string): { incoming: ArchEdge[]; outgoing: ArchEdge[] } {
  return {
    incoming: ARCH_EDGES.filter((e) => e.to === nodeId),
    outgoing: ARCH_EDGES.filter((e) => e.from === nodeId),
  };
}

export function getNode(id: string): ArchNode | undefined {
  return ARCH_NODES.find((n) => n.id === id);
}

/**
 * Compute the set of node ids related to a given node — itself plus every
 * neighbour reached by an incoming or outgoing edge. Used by the map to
 * dim everything else when a node is hovered or focused.
 */
export function relatedNodeIds(nodeId: string): Set<string> {
  const set = new Set<string>([nodeId]);
  for (const e of ARCH_EDGES) {
    if (e.from === nodeId) set.add(e.to);
    if (e.to === nodeId) set.add(e.from);
  }
  return set;
}

export function relatedEdges(nodeId: string): ArchEdge[] {
  return ARCH_EDGES.filter((e) => e.from === nodeId || e.to === nodeId);
}

// -----------------------------------------------------------------------------
// Layout constants — used by ProtocolMap to compute coordinates.
// -----------------------------------------------------------------------------

export const MAP_VIEWBOX = { width: 1200, height: 780 } as const;

/** Vertical band reserved for the layer's title strip on the left. */
export const MAP_LAYER_LABEL_WIDTH = 180;

/** Horizontal padding inside the map for nodes. */
export const MAP_HORIZONTAL_PADDING = 32;

/** Each node's nominal box dimensions in viewBox units. */
export const MAP_NODE_WIDTH = 196;
export const MAP_NODE_HEIGHT = 64;

/**
 * Compute the centre of a node in viewBox coordinates.
 * Layers are stacked top-to-bottom in `ARCH_LAYERS` order.
 */
export function nodeCentre(node: ArchNode): { x: number; y: number } {
  const layerIndex = ARCH_LAYERS.findIndex((l) => l.id === node.layer);
  const layerCount = ARCH_LAYERS.length;
  const layerHeight = MAP_VIEWBOX.height / layerCount;
  const y = layerIndex * layerHeight + layerHeight / 2;

  const peers = nodesByLayer(node.layer);
  const slots = peers.length;
  const usableWidth =
    MAP_VIEWBOX.width - MAP_LAYER_LABEL_WIDTH - MAP_HORIZONTAL_PADDING * 2;
  const slotWidth = usableWidth / slots;
  const x =
    MAP_LAYER_LABEL_WIDTH +
    MAP_HORIZONTAL_PADDING +
    node.slot * slotWidth +
    slotWidth / 2;

  return { x, y };
}

/**
 * Compute the SVG path for an edge between two nodes as a cubic Bézier
 * that exits the bottom of `from` and enters the top of `to`.
 */
export function edgePath(from: ArchNode, to: ArchNode): string {
  const a = nodeCentre(from);
  const b = nodeCentre(to);
  // Exit/enter from the bottom-of-from and top-of-to:
  const ay = a.y + MAP_NODE_HEIGHT / 2;
  const by = b.y - MAP_NODE_HEIGHT / 2;
  const dy = (by - ay) * 0.55;
  return `M ${a.x} ${ay} C ${a.x} ${ay + dy}, ${b.x} ${by - dy}, ${b.x} ${by}`;
}
